/* eslint-disable class-methods-use-this */
import { css, html, LitElement } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { when } from 'lit/directives/when.js';

declare global {
  interface Window {
    MatomoConsent: any;
  }
}

interface MatomoSettings {
  divId: string;
  useSecureCookies: boolean;
  cookiePath?: string;
  cookieDomain?: string;
  cookieSameSite: string;
  OptOutComplete: string;
  YouMayOptOut: string;
  YouAreOptedIn: string;
  YouAreOptedOut: string;
}

@customElement('umb-matomo-opt-out')
export class UmbMatomoOptOut extends LitElement {
  @state()
  private _consent = false;

  @state()
  private _errorMessage?: string;

  #host: ShadowRoot;

  #settings: MatomoSettings = {
    divId: 'matomo-opt-out',
    useSecureCookies: true,
    cookieSameSite: 'Lax',
    OptOutComplete:
      'Opt-out complete; your visits to this website will not be recorded by the web analytics tool. Note that if you clear your cookies, delete the opt-out cookie, or if you change computers or browsers, you will need to opt-out again.',
    YouMayOptOut:
      'You may choose to prevent this website from aggregating and analyzing the actions you take here. Doing so will protect your privacy, but will also prevent the owner from learning from your actions and creating a better experience for you and other users.',
    YouAreOptedIn: 'You are opted in. Toggle to opt-out.',
    YouAreOptedOut: 'You are opted out. Toggle to opt-in.',
  };

  constructor(host: ShadowRoot) {
    super();

    this.#host = host;

    window.MatomoConsent = {
      cookiesDisabled: !navigator || !navigator.cookieEnabled,
      CONSENT_COOKIE_NAME: 'mtm_consent',
      CONSENT_REMOVED_COOKIE_NAME: 'mtm_consent_removed',
      DEFAULT_MS_TO_EXPIRE: -129600000,
      LONG_EXPIRY: 946080000000,
      cookieIsSecure: false,
      useSecureCookies: true,
      cookiePath: '',
      cookieDomain: '',
      cookieSameSite: 'Lax',
      init(
        useSecureCookies: boolean,
        cookiePath: string,
        cookieDomain: string,
        cookieSameSite: string
      ) {
        this.useSecureCookies = useSecureCookies;
        this.cookiePath = cookiePath;
        this.cookieDomain = cookieDomain;
        this.cookieSameSite = cookieSameSite;
        if (useSecureCookies && window.location.protocol !== 'https:') {
          console.log(
            'Error with setting useSecureCookies: You cannot use this option on http.'
          );
        } else {
          this.cookieIsSecure = useSecureCookies;
        }
      },
      hasConsent() {
        const consentCookie = this.getCookie(this.CONSENT_COOKIE_NAME);
        const removedCookie = this.getCookie(this.CONSENT_REMOVED_COOKIE_NAME);
        if (!consentCookie && !removedCookie) {
          return true; // No cookies set, so opted in
        }
        if (removedCookie && consentCookie) {
          this.setCookie(
            this.CONSENT_COOKIE_NAME,
            '',
            this.DEFAULT_MS_TO_EXPIRE
          );
          return false;
        }
        return !!consentCookie || consentCookie !== 0;
      },
      consentGiven() {
        this.setCookie(
          this.CONSENT_REMOVED_COOKIE_NAME,
          '',
          this.DEFAULT_MS_TO_EXPIRE
        );
        this.setCookie(
          this.CONSENT_COOKIE_NAME,
          new Date().getTime(),
          this.LONG_EXPIRY
        );
      },
      consentRevoked() {
        this.setCookie(this.CONSENT_COOKIE_NAME, '', this.DEFAULT_MS_TO_EXPIRE);
        this.setCookie(
          this.CONSENT_REMOVED_COOKIE_NAME,
          new Date().getTime(),
          this.LONG_EXPIRY
        );
      },
      getCookie(cookieName: string) {
        const cookiePattern = new RegExp(`(^|;)[ ]*${cookieName}=([^;]*)`);
        const cookieMatch = cookiePattern.exec(document.cookie);
        return cookieMatch ? window.decodeURIComponent(cookieMatch[2]) : 0;
      },
      setCookie(
        cookieName: string,
        value: string | number,
        msToExpire: number
      ) {
        const expiryDate = new Date();
        expiryDate.setTime(new Date().getTime() + msToExpire);
        document.cookie = `${cookieName}=${window.encodeURIComponent(value)}${
          msToExpire ? `;expires=${expiryDate.toUTCString()}` : ''
        };path=${this.cookiePath || '/'}${
          this.cookieDomain ? `;domain=${this.cookieDomain}` : ''
        }${this.cookieIsSecure ? ';secure' : ''};SameSite=${
          this.cookieSameSite
        }`;
        if (
          (!msToExpire || msToExpire >= 0) &&
          this.getCookie(cookieName) !== String(value)
        ) {
          console.log(
            `There was an error setting cookie '${cookieName}'. Please check domain and path.`
          );
        }
      },
    };
  }

  init() {
    if (!navigator?.cookieEnabled) {
      this._errorMessage =
        'The tracking opt-out feature requires cookies to be enabled.';
    } else if (window.location.protocol !== 'https:') {
      this._errorMessage =
        'The tracking opt-out feature may not work because this site was not loaded over HTTPS. Please reload the page to check if your opt out status changed.';
    } else {
      window.MatomoConsent.init(
        this.#settings.useSecureCookies,
        this.#settings.cookiePath,
        this.#settings.cookieDomain,
        this.#settings.cookieSameSite
      );

      this._consent = window.MatomoConsent.hasConsent();
    }

    this.#host.querySelector('article')?.prepend(this);
  }

  get toggleLabel() {
    return this._consent
      ? `${this.#settings.YouAreOptedIn}`
      : `${this.#settings.YouAreOptedOut}`;
  }

  #onToggleClick() {
    this._consent = !this._consent;

    if (this._consent) {
      window.MatomoConsent.consentGiven();
    } else {
      window.MatomoConsent.consentRevoked();
    }
  }

  render() {
    if (this._errorMessage) {
      return html`${this._errorMessage}`;
    }

    return html`
      ${when(
        this._consent,
        () =>
          html`<p>
            ${this.#settings.YouMayOptOut}
          </p>`,
        () =>
          html`<p>
            ${this.#settings.OptOutComplete}
          </p>`
      )}
      <uui-toggle .checked=${this._consent} @change=${this.#onToggleClick}>${this.toggleLabel}</uui-toggle>
    `;
  }

  static styles = [
    css`
      :host {
        display: block;
        margin-bottom: var(--s-spacer);
      }
    `,
  ];

}
